import React, { useState } from 'react';
import axios from 'axios';
import { CreditCard, Loader2, Shield, Euro, ArrowRight, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

const PaymentCheckoutModal = ({
  isOpen,
  onClose,
  transactionType, // 'offer_payment' or 'deal_payment'
  referenceId,     // offer_id or deal_id
  amount,          // Total amount to pay
  description,     // Description of what's being purchased
  propertyName,    // Property name
  details          // Additional details (dates, nights, etc.)
}) => {
  const [loading, setLoading] = useState(false);

  // Calculate commission (15%)
  const platformFee = amount * 0.15;
  const ownerPayout = amount * 0.85;

  const handleCheckout = async () => {
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const originUrl = window.location.origin;
      
      const response = await axios.post(
        `${BACKEND_URL}/api/payments/checkout/create`,
        {
          transaction_type: transactionType,
          reference_id: referenceId,
          origin_url: originUrl
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('URL checkout non ricevuto');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante la creazione del pagamento');
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-blue-600" />
            Conferma Pagamento
          </DialogTitle>
          <DialogDescription>
            Completa il pagamento in modo sicuro con Stripe
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Transaction Details */}
          <div className="bg-slate-50 rounded-xl p-4">
            <h3 className="font-semibold text-slate-900 mb-3">{description}</h3>
            {propertyName && (
              <p className="text-sm text-slate-600 mb-2">
                🏨 {propertyName}
              </p>
            )}
            {details && (
              <div className="text-sm text-slate-600 space-y-1">
                {details.checkIn && (
                  <p>📅 Check-in: {details.checkIn}</p>
                )}
                {details.checkOut && (
                  <p>📅 Check-out: {details.checkOut}</p>
                )}
                {details.nights && (
                  <p>🌙 {details.nights} notti</p>
                )}
                {details.guests && (
                  <p>👥 {details.guests} ospiti</p>
                )}
              </div>
            )}
          </div>

          {/* Price Breakdown */}
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <Euro className="w-4 h-4" />
              Riepilogo Importi
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between text-slate-700">
                <span>Totale prenotazione</span>
                <span className="font-semibold">€{amount.toFixed(2)}</span>
              </div>
              
              <div className="border-t border-blue-200 my-2"></div>
              
              <div className="flex justify-between text-sm text-slate-600">
                <span className="flex items-center gap-1">
                  <Info className="w-3 h-3" />
                  Commissione TravelDiscount (15%)
                </span>
                <span>€{platformFee.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm text-slate-600">
                <span>Al proprietario</span>
                <span>€{ownerPayout.toFixed(2)}</span>
              </div>

              <div className="border-t border-blue-200 mt-3 pt-3">
                <div className="flex justify-between text-lg font-bold text-blue-900">
                  <span>Da pagare ora</span>
                  <span>€{amount.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Security Badge */}
          <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
            <Shield className="w-4 h-4 text-emerald-600" />
            <span>Pagamento sicuro con</span>
            <Badge variant="outline" className="border-slate-300">
              Stripe
            </Badge>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3">
            <Button
              onClick={handleCheckout}
              disabled={loading}
              className="w-full h-14 text-lg bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Reindirizzamento a Stripe...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5 mr-2" />
                  Paga €{amount.toFixed(2)}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
            
            <Button
              onClick={onClose}
              variant="ghost"
              disabled={loading}
              className="w-full"
            >
              Annulla
            </Button>
          </div>

          {/* Info */}
          <p className="text-xs text-center text-slate-500">
            Cliccando "Paga" sarai reindirizzato alla pagina di pagamento sicura di Stripe.
            <br />
            Accettiamo tutte le principali carte di credito e debito.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentCheckoutModal;
